import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import useWhatsAppNumber from '../../hooks/useWhatsAppNumber';

function PageShell({ title, children }) {
  return (
    <div className="max-w-3xl mx-auto px-4 py-10 md:py-14">
      <h1 className="text-2xl md:text-3xl font-bold text-brand-dark mb-8 pb-4 border-b border-gray-100">{title}</h1>
      <div className="prose prose-gray max-w-none text-gray-600 leading-relaxed space-y-4">
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-lg font-bold text-brand-dark mt-8 mb-3">{title}</h2>
      {children}
    </div>
  );
}

export function FaqPage() {
  const faqs = [
    { q: 'How long does shipping take?', a: 'Orders are typically processed within 1–3 business days and delivered within 4–8 business days, depending on your location.' },
    { q: 'Do you offer Cash on Delivery (COD)?', a: 'No, Cash on Delivery (COD) is currently not available. Please complete your purchase using our prepaid payment options.' },
    { q: 'Is TheYellove.in a trustworthy website?', a: 'Yes. TheYellove.in is a registered Indian business with a valid GST registration and company registration. We provide secure payment options, GST-compliant invoices, and dedicated customer support to ensure a safe and reliable shopping experience.\n\nGSTIN: 33AAMCT7440A1ZK' },
    { q: 'Can I customize a product?', a: 'Yes! We offer customization on selected products. Please check the product description for customization options.' },
    { q: 'Can I cancel my order?', a: 'Orders can only be cancelled before they are dispatched. Once shipped, cancellations are not possible.' },
    { q: 'Can I cancel a customized or personalized order?', a: 'No. As customized and personalized products are made specifically for you, cancellations, modifications, returns, or refunds are not possible once the order has been placed.' },
    { q: 'What is your return policy?', a: 'Returns or replacements are accepted only if the product is damaged during transit.' },
    { q: 'How do I claim a replacement for a damaged product?', a: 'You must provide a clear, uninterrupted 360° unboxing video starting before opening the parcel and ending after the product has been fully inspected. Claims without an unboxing video cannot be processed.' },
    { q: 'Do you accept returns for change of mind?', a: 'No. We do not accept returns, exchanges, or refunds for reasons other than transit damage.' },
    { q: 'Will I receive the exact product shown in the pictures?', a: 'We strive to ensure product images accurately represent the item. However, slight variations in color, finish, or appearance may occur due to screen settings, lighting conditions, and the handmade/customized nature of some products.' },
    { q: 'How can I contact customer support?', a: 'You can reach us through WhatsApp, email, or the Contact Us page on our website.' },
    { q: 'Do you sell officially licensed anime merchandise?', a: 'Product licensing information, where applicable, is mentioned on the respective product pages.' },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 md:py-14">
      <h1 className="text-2xl md:text-3xl font-bold text-brand-dark mb-8">Frequently Asked Questions</h1>
      <div className="space-y-3">
        {faqs.map((faq, i) => (
          <details key={i} className="bg-white rounded-2xl border border-gray-100 group">
            <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-brand-dark list-none">
              {faq.q}
              <span className="text-gray-400 group-open:rotate-45 transition-transform text-xl leading-none flex-shrink-0 ml-3">+</span>
            </summary>
            <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed space-y-1">
              {faq.a.split('\n').map((line, j) => <p key={j}>{line}</p>)}
            </div>
          </details>
        ))}
      </div>
      <div className="mt-10 bg-brand-yellow-light rounded-2xl p-6 text-center">
        <p className="font-semibold text-brand-dark mb-2">Still have questions?</p>
        <p className="text-gray-500 text-sm mb-4">Our team is happy to help you</p>
        <Link to="/contact" className="btn-primary px-6 py-2.5 rounded-xl font-semibold text-sm inline-block">Contact Us</Link>
      </div>
    </div>
  );
}

export function ContactPage() {
  const whatsappNumber = useWhatsAppNumber();
  return (
    <div className="max-w-4xl mx-auto px-4 py-10 md:py-14">
      <h1 className="text-2xl md:text-3xl font-bold text-brand-dark mb-8">Contact Us</h1>
      <div className="grid md:grid-cols-2 gap-8">
        {/* Contact info */}
        <div className="space-y-6">
          <p className="text-gray-600">We'd love to hear from you! Reach out to us through any of the channels below and we'll get back to you as soon as possible.</p>
          {[
            { icon: MessageCircle, label: 'WhatsApp', value: 'Chat with us', sub: 'Fastest response — usually within minutes', color: 'text-green-500', href: `https://wa.me/${whatsappNumber}` },
            { icon: Mail, label: 'Email', value: 'support@theyellove.in', sub: 'We reply within 24 hours', color: 'text-brand-yellow', href: 'mailto:support@theyellove.in' },
            { icon: Phone, label: 'Phone', value: '+91 72002 52350', sub: 'Mon–Sat, 10 AM – 7 PM', color: 'text-brand-dark', href: 'tel:+917200252350' },
            { icon: MapPin, label: 'Address', value: 'Chennai, Tamil Nadu, India', sub: 'No walk-ins, online only', color: 'text-brand-red', href: null },
          ].map(item => {
            const Icon = item.icon;
            return (
              <div key={item.label} className="flex gap-4">
                <div className={`w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0`}>
                  <Icon size={18} className={item.color} />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                  {item.href
                    ? <a href={item.href} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-dark hover:text-brand-yellow transition-colors">{item.value}</a>
                    : <p className="font-semibold text-brand-dark">{item.value}</p>
                  }
                  <p className="text-xs text-gray-400 mt-0.5">{item.sub}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick message form (informational — no backend needed for MVP) */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-bold text-brand-dark mb-5">Send us a message</h3>
          <div className="space-y-4">
            <input placeholder="Your name" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-yellow" />
            <input placeholder="Your email" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-yellow" type="email" />
            <input placeholder="Subject" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-yellow" />
            <textarea placeholder="Your message..." rows={4} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-yellow resize-none" />
            <a
              href="mailto:support@theyellove.in"
              className="btn-primary w-full py-3 rounded-xl font-semibold text-center block"
            >
              Send Message
            </a>
            <p className="text-xs text-gray-400 text-center">This will open your email client</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AboutPage() {
  return (
    <PageShell title="About TheYellove">
      <div className="bg-gradient-to-br from-brand-yellow-light to-brand-cream-light rounded-2xl p-6 mb-6 text-center">
        <div className="text-5xl mb-3">💛</div>
        <p className="text-brand-dark font-semibold text-lg">From The Founder</p>
      </div>
      <div className="space-y-4 text-gray-700 leading-relaxed">
        <p>As a digital marketer, I've had the privilege of working with many brands and helping them grow. Along the way, I discovered a passion for creating something of my own—a brand built around creativity, meaningful moments, and the joy of giving.</p>
        <p>That's how Theyellove was born.</p>
        <p>What began as a simple idea soon grew into a destination for personalized gifts, custom keepsakes, cake toppers, baby milestone products, and thoughtful creations designed to make every celebration more special. Every product is crafted with care, attention to detail, and the belief that even the smallest gifts can create lasting memories.</p>
        <p>At Theyellove, we believe that the most meaningful gifts are the ones that tell a story. Whether you're celebrating a birthday, welcoming a new baby, or surprising someone you love, our goal is to help you turn special moments into cherished memories.</p>
        <p>Thank you for supporting our journey and allowing us to be a small part of yours.</p>
        <p className="mt-6">With love,<br /><span className="font-bold text-brand-dark">Founder, Theyellove 💛</span></p>
      </div>

      <Section title="Our Mission">
        <p>Our mission is to bring joy through thoughtfully chosen products that make everyday life feel more aesthetic, fun, and personal.</p>
        <p>We aim to create an experience where every product feels:</p>
        <ul className="space-y-1 text-sm mt-2">
          <li>✨ Cute</li>
          <li>✨ High quality</li>
          <li>✨ Unique</li>
          <li>✨ Gift-worthy</li>
          <li>✨ Worth obsessing over</li>
        </ul>
      </Section>

      <Section title="Our Vision">
        <p>To become a go-to lifestyle brand for people who love cute culture, aesthetic living, gaming-inspired products, and creative self-expression.</p>
        <p>We envision Theyellove as a happy little universe where people discover products that light up their rooms, desks, moods, and personalities 💫</p>
      </Section>

      <div className="text-center mt-8">
        <Link to="/products" className="btn-primary px-8 py-3 rounded-xl font-semibold inline-block">Shop Now</Link>
      </div>
    </PageShell>
  );
}

export function PrivacyPolicyPage() {
  return (
    <PageShell title="Privacy Policy">
      <p className="text-gray-600 mb-6">At TheYellove.in, we value your privacy and are committed to protecting your personal information.</p>

      <Section title="1. Information We Collect">
        <p>We may collect the following information when you use our website or place an order:</p>
        <ul className="list-disc list-inside space-y-1 text-sm mt-2">
          <li>Name</li>
          <li>Mobile Number</li>
          <li>Email Address</li>
          <li>Billing and Shipping Address</li>
          <li>Order Details</li>
          <li>Payment Information (processed securely through third-party payment gateways)</li>
        </ul>
      </Section>

      <Section title="2. How We Use Your Information">
        <p>We use your information to:</p>
        <ul className="list-disc list-inside space-y-1 text-sm mt-2">
          <li>Process and fulfill your orders</li>
          <li>Send order confirmations and shipping updates</li>
          <li>Respond to your queries and customer support requests</li>
          <li>Improve our products, services, and website experience</li>
          <li>Send promotional offers and marketing communications (only where permitted)</li>
        </ul>
      </Section>

      <Section title="3. Payment Security">
        <p>All payments are processed securely through trusted payment gateways such as Razorpay. We do not store your complete card details, UPI PINs, or banking credentials on our servers.</p>
      </Section>

      <Section title="4. Sharing of Information">
        <p>We may share necessary information with trusted third-party service providers, including:</p>
        <ul className="list-disc list-inside space-y-1 text-sm mt-2">
          <li>Payment processors</li>
          <li>Shipping and logistics partners</li>
          <li>Technology and website service providers</li>
        </ul>
        <p className="mt-2">Your information is shared only to the extent required to provide our services.</p>
      </Section>

      <Section title="5. Cookies">
        <p>We use cookies and similar technologies to:</p>
        <ul className="list-disc list-inside space-y-1 text-sm mt-2">
          <li>Keep you logged in</li>
          <li>Remember your shopping cart</li>
          <li>Improve website performance and user experience</li>
        </ul>
        <p className="mt-2">You may disable cookies through your browser settings; however, some website features may not function properly.</p>
      </Section>

      <Section title="6. Data Security">
        <p>We implement reasonable security measures to protect your personal information from unauthorized access, disclosure, or misuse.</p>
      </Section>

      <Section title="7. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with the revised update date.</p>
      </Section>

      <Section title="8. Contact Us">
        <p>If you have any questions regarding this Privacy Policy, please contact us at:</p>
        <p className="mt-2">Email: <a href="mailto:support@theyellove.in" className="text-brand-yellow hover:underline">support@theyellove.in</a></p>
        <p>Website: <a href="https://www.theyellove.in" className="text-brand-yellow hover:underline">www.theyellove.in</a></p>
      </Section>
    </PageShell>
  );
}

export function TermsPage() {
  return (
    <PageShell title="Terms & Conditions">
      <p className="text-sm text-gray-400 mb-6">Last Updated: May 2026</p>

      <Section title="1. Acceptance of Terms">
        <p>By accessing and using TheYellove.in, you agree to be bound by these Terms & Conditions. Please review them carefully before placing an order.</p>
      </Section>

      <Section title="2. Products & Pricing">
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li>All prices are listed in Indian Rupees (INR) and are inclusive of applicable taxes unless otherwise stated.</li>
          <li>We reserve the right to modify prices, product details, and availability without prior notice.</li>
          <li>Product images are for illustrative purposes. Minor variations in color, finish, or appearance may occur.</li>
        </ul>
      </Section>

      <Section title="3. Orders & Payment">
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li>Orders are confirmed only after successful payment.</li>
          <li>We accept UPI, Credit Cards, Debit Cards, Net Banking, and other payment methods available at checkout.</li>
          <li>All online transactions are processed securely through trusted payment gateways.</li>
        </ul>
      </Section>

      <Section title="4. Cancellations">
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li>Orders may be cancelled only before they are dispatched.</li>
          <li>Once an order has been packed or shipped, cancellation requests cannot be accepted.</li>
          <li>Customized or personalized products cannot be cancelled, modified, returned, or refunded once the order has been placed.</li>
        </ul>
      </Section>

      <Section title="5. Returns & Replacements">
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li>Returns or replacements are accepted only for products damaged during transit.</li>
          <li>A clear, uninterrupted 360° unboxing video is mandatory for all damage claims.</li>
          <li>Claims submitted without an unboxing video may not be eligible for replacement or refund.</li>
        </ul>
      </Section>

      <Section title="6. Intellectual Property">
        <p>All content on TheYellove.in, including images, designs, logos, text, and graphics, is the property of TheYellove.in or its respective owners and may not be copied, reproduced, or distributed without permission.</p>
      </Section>

      <Section title="7. Limitation of Liability">
        <p>TheYellove.in shall not be liable for any indirect, incidental, or consequential damages arising from the use of our products, services, or website.</p>
      </Section>

      <Section title="8. Contact Us">
        <p>For any questions regarding these Terms & Conditions, please contact us at:</p>
        <p className="mt-2">Email: <a href="mailto:support@theyellove.in" className="text-brand-yellow hover:underline">support@theyellove.in</a></p>
      </Section>
    </PageShell>
  );
}

export function ShippingPolicyPage() {
  return (
    <PageShell title="Shipping Policy">
      <Section title="Delivery Timelines">
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { area: 'Chennai', time: '3–4 business days' },
            { area: 'Tamil Nadu', time: '4–6 business days' },
            { area: 'Metro Cities', time: '4–6 business days' },
            { area: 'Rest of India', time: '5–8 business days' },
          ].map(r => (
            <div key={r.area} className="bg-gray-50 rounded-xl p-4">
              <p className="font-semibold text-brand-dark text-sm">{r.area}</p>
              <p className="text-gray-500 text-sm">{r.time}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Shipping Charges">
        <p><span className="font-semibold text-green-600">Free Shipping</span> on orders above ₹499.</p>
      </Section>
      <Section title="Order Processing">
        <p>Orders are processed within 1–3 business days. You'll receive a shipping confirmation with tracking details once your order is dispatched.</p>
      </Section>
      <Section title="Tracking">
        <p>Track your order anytime on our <Link to="/track-order" className="text-brand-yellow hover:underline">Track Order</Link> page using your order number.</p>
      </Section>
      <Section title="Delays">
        <p>Delivery times may be affected during festivals, public holidays, or adverse weather conditions. We'll keep you informed via SMS/email.</p>
      </Section>
    </PageShell>
  );
}

export function RefundPolicyPage() {
  return (
    <PageShell title="Return & Replacement Policy">
      <p className="text-gray-600 mb-6">At TheYellove, every product is carefully inspected and securely packed before dispatch.</p>

      <Section title="Returns & Replacements">
        <p>Returns or replacements are accepted only if the product is damaged during transit.</p>
        <p className="mt-3">To be eligible for a replacement, customers must:</p>
        <ul className="list-disc list-inside space-y-2 text-sm mt-2">
          <li>Record a clear, uninterrupted 360° unboxing video starting before opening the delivered parcel and continuing until the product has been fully unpacked and inspected.</li>
          <li>Ensure the parcel, shipping label, packaging, and damaged product are clearly visible in the video.</li>
          <li>Report the damage within <span className="font-semibold text-brand-dark">24 hours</span> of delivery.</li>
          <li>Provide the unboxing video and clear photos of the damaged item when submitting a claim.</li>
        </ul>
      </Section>

      <Section title="Important">
        <ul className="list-disc list-inside space-y-2 text-sm">
          <li>Claims submitted without a complete 360° unboxing video will not be accepted.</li>
          <li>Edited, paused, or incomplete videos are not eligible for verification.</li>
          <li>Returns, exchanges, refunds, or replacements will not be provided for reasons other than transit damage.</li>
          <li>All claims are subject to verification by our support team.</li>
        </ul>
        <p className="mt-4 text-gray-700">Once the claim is verified and approved, a replacement will be arranged at no additional cost.</p>
      </Section>
    </PageShell>
  );
}

export function CookiePolicyPage() {
  return (
    <PageShell title="Cookie Policy">
      <p className="text-sm text-gray-400 mb-6">Last Updated: June 2026</p>

      <Section title="What Are Cookies?">
        <p>Cookies are small text files stored on your device when you visit a website. They help the website remember your preferences and improve your browsing experience.</p>
      </Section>

      <Section title="How We Use Cookies">
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li><strong>Essential Cookies</strong> — Required for the website to function (e.g., cart, login session). Cannot be disabled.</li>
          <li><strong>Analytics Cookies</strong> — Help us understand how visitors use our site (e.g., Google Analytics). Used only with your consent.</li>
          <li><strong>Marketing Cookies</strong> — Used to show relevant ads (e.g., Meta Pixel). Used only with your consent.</li>
        </ul>
      </Section>

      <Section title="Your Choices">
        <p>When you first visit TheYellove.in, you will be asked to accept or reject non-essential cookies. You can change your preference at any time by clearing your browser's local storage or cookies.</p>
      </Section>

      <Section title="Third-Party Cookies">
        <p>We may use third-party services such as Google Analytics and Meta (Facebook) Pixel which may set their own cookies. These are governed by the respective third-party privacy policies.</p>
      </Section>

      <Section title="Contact Us">
        <p>If you have any questions about our Cookie Policy, please contact us at:</p>
        <p className="mt-2">Email: <a href="mailto:support@theyellove.in" className="text-brand-yellow hover:underline">support@theyellove.in</a></p>
      </Section>
    </PageShell>
  );
}
